<template>
    <v-container
        fluid
        class="pa-6">
        <div class="headline font-weight-bold pb-4">
            {{ $t('menu.andamandaPay') }}
        </div>

        <v-card>
            <v-card-title class="pt-0">
                <v-tabs
                    color="primary"
                    :value="tab"
                    @change="onTabChanged">
                    <v-tab>
                        {{ $t('andamandaPay.pocketId') }}
                    </v-tab>
                    <v-tab>
                        {{ $t('andamandaPay.wristbandId') }}
                    </v-tab>
                </v-tabs>
            </v-card-title>
            <v-card-text>
                <v-tabs-items
                    :value="tab">
                    <v-tab-item>
                        <v-card-actions>
                            <date-range-field
                                class="mr-4"
                                v-model="walletDateRange"
                                @change="fetchWallets"/>
                            <v-btn
                                class="font-weight-regular"
                                color="primary"
                                @click="getWalletReport">
                                <icon
                                    class="mr-2"
                                    value="file_download"
                                    size="18px"/>
                                {{ $t('button.exportXlsx') }}
                            </v-btn>
                        </v-card-actions>
                        <wallet-list-view
                            :wallets="wallets"
                            :pagination="pagination"
                            @wallet:sort="onWalletSort"/>
                    </v-tab-item>
                    <v-tab-item>
                        <v-card-actions>
                            <date-range-field
                                class="mr-4"
                                v-model="wristbandDateRange"
                                @change="fetchWristbands"/>
                            <v-btn
                                class="font-weight-regular"
                                color="primary"
                                @click="getWristbandReport">
                                <icon
                                    class="mr-2"
                                    value="file_download"
                                    size="18px"/>
                                {{ $t('button.exportXlsx') }}
                            </v-btn>
                        </v-card-actions>
                        <wristband-list-view
                            :wristbands="wristbands"
                            :pagination="pagination"
                            @wristband:sort="onWristbandSort"/>
                    </v-tab-item>
                </v-tabs-items>
            </v-card-text>
        </v-card>

        <v-pagination
            v-show="showPagination"
            class="pt-2"
            :style="{
                marginRight: '-8px',
            }"
            :value="page"
            :length="pageTotal"
            :total-visible="totalVisible"
            @input="onPaginationChanged"/>
    </v-container>
</template>

<script>
import WalletListView from '@/views/WalletListView'
import WristbandListView from '@/views/WristbandListView'

import DateRangeField from '@/components/fields/DateRangeField'
import PaginationMixin from '@/mixins/PaginationMixin'

import { mapState } from 'vuex'

export default {
    name: 'AndamandaPayListPage',

    mixins: [PaginationMixin],

    components: {
        WalletListView,
        WristbandListView,
        DateRangeField
    },

    data () {
        return {
            tab: 0,
            walletDateRange: [],
            wristbandDateRange: [],
            walletPagination: {},
            wristbandPagination: {}
        }
    },

    computed: {
        ...mapState('wristband', {
            wristbands: 'wristbands',
            wristbandTotal: 'total'
        }),
        ...mapState('wallet', {
            wallets: 'wallets',
            walletTotal: 'total'
        })
    },

    mounted () {
        this.fetchWallets()
        this.fetchWristbands()
    },

    methods: {
        getWalletQueryObject () {
            return {
                ...this.getQueryObject(),
                from: this.walletDateRange.length === 2 ? this.walletDateRange[0] : undefined,
                to: this.walletDateRange.length === 2 ? this.walletDateRange[1] : undefined
            }
        },

        getWristbandQueryObject () {
            return {
                ...this.getQueryObject(),
                from: this.wristbandDateRange.length === 2 ? this.wristbandDateRange[0] : undefined,
                to: this.wristbandDateRange.length === 2 ? this.wristbandDateRange[1] : undefined
            }
        },

        fetchWallets () {
            this.$store.dispatch('wallet/fetchWallets', this.getWalletQueryObject())
        },

        fetchWristbands () {
            this.$store.dispatch('wristband/fetchWristbands', this.getWristbandQueryObject())
        },

        getWalletReport () {
            const { page, itemsPerPage, ...reportQuery } = this.getWalletQueryObject()
            this.$store.dispatch('wallet/getWalletReport', reportQuery)
        },

        getWristbandReport () {
            const { page, itemsPerPage, ...reportQuery } = this.getWristbandQueryObject()
            this.$store.dispatch('wristband/getWristbandReport', reportQuery)
        },

        persistPaginationToDict (targetDict) {
            targetDict.page = this.page
            targetDict.sortBy = this.sortBy
            targetDict.sortType = this.sortType
            targetDict.itemsPerPage = this.itemsPerPage
            targetDict.totalVisible = this.totalVisible
        },

        updatePaginationValue (pagination) {
            const defaultPagination = this.getDefaultPagination()
            this.page = pagination.page ?? defaultPagination.page
            this.sortBy = pagination.sortBy ?? defaultPagination.sortBy
            this.sortType = pagination.sortType ?? defaultPagination.sortType
            this.itemsPerPage = pagination.itemsPerPage ?? defaultPagination.itemsPerPage
            this.totalVisible = pagination.totalVisible ?? defaultPagination.totalVisible
        },

        onWalletSort (sortBy, sortType) {
            this.sortBy = sortBy ?? null
            this.sortType = sortType ?? null
            this.fetchWallets()
        },

        onWristbandSort (sortBy, sortType) {
            this.sortBy = sortBy ?? null
            this.sortType = sortType ?? null
            this.fetchWristbands()
        },

        onTabChanged (newTab) {
            const paginationMapDict = {
                0: this.walletPagination,
                1: this.wristbandPagination
            }
            const pagination = paginationMapDict?.[this.tab]
            const newPagination = paginationMapDict?.[newTab]

            if (!this.pagination) {
                this.$app.alert(
                    'Error',
                    `Found unregistered pagination! Please contact developer. Error Code ${this.tab}`
                )
            }

            this.persistPaginationToDict(pagination)
            this.updatePaginationValue(newPagination)

            this.tab = newTab
        },

        onPaginationChanged (value) {
            this.page = value
        }
    }
}
</script>

<style lang="scss" scoped>
::v-deep .v-pagination {
    justify-content: right;
    margin-right: -12px;
}
</style>
